import { FC, useEffect, useState } from 'react'

import { cloneDeep, sumBy } from 'lodash'

// Component imports
import ConfirmationCompsWrapper from './ConfirmationCompsWrapper'
import { SelectedEventPrice } from './SelectedEventPrice'

// Redux
import { useAppSelector } from '../../../../../../store/hooks'
import {
  selectRegistration,
  selectRegistrationTicketsR,
  selectTeamTabData,
} from '../../../../../../store/registration/registrationSlice'

import { convertPrice, getFloatPrice } from '../../../../../../helpers/price'
import { getTeamMemberById } from '../../../../../../helpers/teamMembers'

import useRegistrationTickets from '../../../../../../hooks/useRegistrationTickets'

import { IRegistrationEventTab } from '../../../../../../types/competitor_types'
import { IUserInterface } from '../../../../../../models/users/user.interface'
import { IRegistrationTicketInterface } from '../../../../../../models/registration-tickets/registrationTicket.interface'

import { CONST } from '../../../../../../const/const'

type SelectedEventTicketListProps = {
  setEventTab: (eventTab: IRegistrationEventTab) => void
  isSplitBill: boolean
  registrar: IUserInterface | null
  currency: string
}

const SelectedEventTicketList: FC<SelectedEventTicketListProps> = ({
  setEventTab,
  isSplitBill,
  registrar,
  currency,
}) => {
  const { onUpdateTickets } = useRegistrationTickets()

  const teamTabData = useAppSelector(selectTeamTabData)
  const registration = useAppSelector(selectRegistration)
  const registrationTickets = useAppSelector(selectRegistrationTicketsR)

  const [open, setOpen] = useState(false)
  const [selectedUnits, setSelectedUnits] = useState<{
    totalItems: number
    totalPrice: number
  }>({ totalItems: 0, totalPrice: 0 })
  const [isSendBillToAllChecked, setIsSendBillToAllChecked] = useState(false)

  useEffect(() => {
    const totalPrice = sumBy(registrationTickets, (ticket) => convertPrice(ticket.ticketPrice))
    setSelectedUnits({
      totalItems: registrationTickets.length || 0,
      totalPrice: totalPrice / 100,
    })
  }, [registrationTickets])

  const onAssignTicketChange = async (item: IRegistrationTicketInterface, id: string) => {
    if (!registrar) return

    setIsSendBillToAllChecked(false)

    const teamMember = getTeamMemberById(registrar, teamTabData, id)
    const registrationTicketsToUpdate = [
      {
        ...item,
        ownerId: teamMember?.memberId || null,
        ownerName: teamMember?.memberName || null,
        ownerProfilePicture: teamMember?.memberProfilePicture || null,
        recipientId: registration?.userId || null,
        recipientName: registration?.userName || null,
        recipientProfilePicture: registration?.userProfilePicture || null,
      },
    ]

    await onUpdateTickets({
      registrationTicketsToUpdate,
    })
  }

  const onRecipientChange = async (item: IRegistrationTicketInterface, isChecked: boolean) => {
    if (!item?.ownerId || !registrar) return

    const teamMember = getTeamMemberById(registrar, teamTabData, item?.ownerId)
    const registrationTicketsToUpdate = [
      {
        ...item,
        recipientId: (isChecked ? teamMember?.memberId : registration?.userId) || null,
        recipientName: (isChecked ? teamMember?.memberName : registration?.userName) || null,
        recipientProfilePicture:
          (isChecked ? teamMember?.memberProfilePicture : registration?.userProfilePicture) || null,
      },
    ]

    await onUpdateTickets({
      registrationTicketsToUpdate,
    })
  }

  const onSendBillToAll = async (isChecked: boolean) => {
    if (!registrar) return

    setIsSendBillToAllChecked(!isSendBillToAllChecked)

    let registrationTicketsToUpdate = cloneDeep(registrationTickets)

    registrationTicketsToUpdate = registrationTicketsToUpdate.map((item) => {
      if (!item?.ownerId || !registrar) return item

      const teamMember = getTeamMemberById(registrar, teamTabData, item?.ownerId)

      return {
        ...item,
        recipientId: isChecked ? teamMember?.memberId || null : registration?.userId || null,
        recipientName: isChecked ? teamMember?.memberName || null : registration?.userName || null,
        recipientProfilePicture: isChecked
          ? teamMember?.memberProfilePicture || null
          : registration?.userProfilePicture || null,
      }
    })

    await onUpdateTickets({
      registrationTicketsToUpdate,
    })
  }

  // Group by feesItemId
  const groupedTickets = registrationTickets.reduce<Record<string, IRegistrationTicketInterface[]>>(
    (acc, ticket) => {
      if (!acc[ticket.ticketItemId || '']) acc[ticket.ticketItemId || ''] = []
      acc[ticket.ticketItemId || ''].push(ticket)
      return acc
    },
    {}
  )

  return (
    <ConfirmationCompsWrapper
      title="Tickets"
      setEventTab={setEventTab}
      redirectTab={CONST.UI.REGISTER.TABS.TICKETS}
      accordion={{ open, setOpen }}
      selected_units={`${selectedUnits.totalItems} ${selectedUnits.totalItems > 1 ? 'tickets' : 'ticket'}, ${getFloatPrice(`${currency}${selectedUnits.totalPrice}`)}`}
      showList={!!registrationTickets.length}
      isSplitBill={isSplitBill}
      isSendBillToAllChecked={isSendBillToAllChecked}
      onSendBillToAll={onSendBillToAll}
    >
      {Object.entries(groupedTickets).map(([_, items]) =>
        registrationTickets.map((item, index) => (
          <div
            key={index}
            className="w-full flex flex-wrap flex-col sm:flex-row gap-3 sm:items-center sm:justify-between p-4 hover:bg-gray-50 transition-colors border-b border-gray-100"
          >
            <div>
              <p className="font-medium text-gray-900">{item.ticketTitle}</p>
              <p className="text-sm text-gray-600">
                Item {index + 1} of {items.length}
              </p>
            </div>
            <SelectedEventPrice
              onRecipientChange={(state) => onRecipientChange(item, state)}
              recipientId={item?.recipientId || null}
              onAssignChange={(id) => onAssignTicketChange(item, id)}
              ownerId={item?.ownerId || ''}
              price={getFloatPrice(`${currency}${item.ticketPrice}`)}
              isSplitBill={isSplitBill}
              showAssignTo={true}
            />
          </div>
        ))
      )}
    </ConfirmationCompsWrapper>
  )
}

export default SelectedEventTicketList
